import type { OpenWhenLetter, SectionRow } from '@/types'

import { useState } from 'react'
import { cn } from '@/lib/utils'
import { Heart, Mail, MailOpen } from 'lucide-react'

import {
    Dialog,
    DialogClose,
    DialogContent,
    DialogTitle
} from '@/components/ui/dialog'

import Reveal from '@/components/landing/reveal'
import SectionHeading from '@/components/landing/section-heading'

interface OpenWhenProps {
    section?: SectionRow
    letters: Array<OpenWhenLetter>
}

const PAPERS = [
    'from-[#fde8ee] to-[#f9d0dc]',
    'from-[#fff1de] to-[#fbdcb8]',
    'from-[#efe6fb] to-[#dccbf3]',
    'from-[#e4f3ec] to-[#c9e8d8]',
    'from-[#fde8ee] to-[#f6c3d1]',
    'from-[#e6f0fb] to-[#cadff4]'
]

export default function OpenWhen({ section, letters }: OpenWhenProps) {
    const [activeId, setActiveId] = useState<string | null>(null)
    const [lastId, setLastId] = useState<string | null>(null)
    const [opened, setOpened] = useState<Array<string>>([])
    const active = letters.find(letter => letter.id === (activeId ?? lastId))

    const openLetter = (id: string) => {
        setActiveId(id)
        setLastId(id)
        setOpened(current => current.includes(id) ? current : [...current, id])
    }

    return (
        <div id='open-when' className='scroll-mt-8 rounded-[2.5rem] bg-white/70 px-6 py-12 shadow-[0_30px_70px_-40px_rgba(91,21,48,0.5)] ring-1 ring-blush backdrop-blur-sm sm:px-10 sm:py-14'>
            <SectionHeading section={section} level='h3' size='md' />

            <ul className='mt-10 grid grid-cols-2 gap-4 sm:gap-5 md:grid-cols-3'>
                {letters.map((letter, index) => {
                    const wasOpened = opened.includes(letter.id)

                    return (
                        <li key={letter.id}>
                            <Reveal delay={(index % 3) * 90} className='h-full'>
                                <button
                                    type='button'
                                    onClick={() => openLetter(letter.id)}
                                    className={cn(
                                        'group relative flex h-full w-full flex-col overflow-hidden rounded-3xl bg-gradient-to-br p-4 text-left shadow-[0_16px_34px_-22px_rgba(91,21,48,0.7)] transition duration-300 outline-none hover:-translate-y-1 hover:shadow-[0_26px_40px_-22px_rgba(91,21,48,0.75)] focus-visible:ring-4 focus-visible:ring-petal/40 sm:p-5',
                                        PAPERS[index % PAPERS.length]
                                    )}
                                >
                                    <span aria-hidden className='absolute inset-x-0 top-0 h-14 bg-white/35 [clip-path:polygon(0_0,100%_0,50%_100%)]' />

                                    <span className='relative grid size-11 place-items-center rounded-full bg-white text-petal-deep shadow-sm transition-transform group-hover:scale-110'>
                                        {wasOpened ? <MailOpen aria-hidden className='size-5' /> : <Mail aria-hidden className='size-5' />}
                                    </span>

                                    {letter.title && (
                                        <span
                                            data-mdlfy-cms={`open_when_letters:${letter.id}:title`}
                                            className='relative mt-4 block font-hand text-[1.45rem] leading-tight font-semibold text-wine sm:text-2xl'
                                        >
                                            {letter.title}
                                        </span>
                                    )}

                                    <span className='relative mt-auto pt-4 text-xs font-bold tracking-[0.2em] text-petal-deep/80 uppercase'>
                                        {wasOpened ? 'Phir se padho' : 'Kholo'}
                                    </span>
                                </button>
                            </Reveal>
                        </li>
                    )
                })}
            </ul>

            <Dialog open={activeId !== null} onOpenChange={next => { if (!next) setActiveId(null) }}>
                <DialogContent
                    className='letter-paper w-[min(92vw,520px)] max-w-none gap-0 rounded-2xl p-0 sm:max-w-none'
                    overlayClassName='bg-night/70 supports-backdrop-filter:backdrop-blur-sm'
                    closeClassName='top-3 right-3 bg-white/80 text-wine hover:bg-white'
                >
                    {active && (
                        <div className='px-7 pt-12 pb-8 sm:px-10'>
                            <span aria-hidden className='absolute -top-6 left-1/2 grid size-12 -translate-x-1/2 place-items-center rounded-full bg-gradient-to-br from-petal to-petal-deep shadow-lg ring-4 ring-[#fffdf8]'>
                                <Heart className='size-5 fill-cream text-cream' />
                            </span>

                            <DialogTitle className='pl-6 font-hand text-3xl leading-9 font-semibold text-wine sm:pl-4'>
                                {active.title && (
                                    <span data-mdlfy-cms={`open_when_letters:${active.id}:title`}>
                                        {active.title}
                                    </span>
                                )}
                            </DialogTitle>

                            {active.message && (
                                <p
                                    data-mdlfy-cms={`open_when_letters:${active.id}:message`}
                                    className='mt-4 pl-6 font-hand text-[1.4rem] leading-9 whitespace-pre-line text-ink sm:pl-4 sm:text-[1.55rem]'
                                >
                                    {active.message}
                                </p>
                            )}

                            <div className='mt-8 flex justify-end'>
                                <DialogClose className='inline-flex items-center gap-2 rounded-full bg-wine px-5 py-2.5 text-sm font-bold text-cream transition outline-none hover:bg-petal-deep focus-visible:ring-4 focus-visible:ring-petal/40'>
                                    <Heart aria-hidden className='size-4 fill-current' />
                                    Dil mein rakh liya
                                </DialogClose>
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    )
}
